import React, { useState } from "react";
import { Box, Button, Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LOCALIZATION } from "../../utils/localization";
import "./BlogContent.scss"; // reuses styling (boldedText, linkedText, etc.)

const TemplesContent: React.FC<any> = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Box>
      <h1>Famous Temples in India</h1>
      <p className="lede">Explore India’s Sacred Shrines with Temple Dekho</p>

      <Collapse in={expanded} collapsedSize={100}>
        <h2>Discover the Divine</h2>
        <p>
          India is home to countless temples, each one a living testament to the
          country’s spiritual legacy, architectural grandeur and deep-rooted
          devotion. At Temple Dekho, we invite you to explore these revered
          spaces, understand their unique significance, and stay connected even
          when you can’t physically visit.
        </p>

        <h2>Why these temples?</h2>
        <p>
          Every temple has its own story, the legend behind its origins, the
          gods it honours, the rituals upheld through centuries. From the
          flame-lit sanctums to the rhythmic ringing of bells, these sacred
          places connect us to something timeless and true. Through Temple
          Dekho, you get:
        </p>
        <ul>
          <li>
            Insightful information on each temple’s history, rituals, and
            festivals
          </li>
          <li>
            The ability to book pujas or live darshans from wherever you are
          </li>
          <li>
            A way to feel rooted in faith even when distance separates you
          </li>
        </ul>

        <h2>Featured Temples</h2>
        <p>
          Here are a few of the many temples in India you can explore through
          our platform:
        </p>

        <h3>Kamakhya Temple, Guwahati (Assam)</h3>
        <p>
          Perched atop the Nilachal Hill, Kamakhya Devi Temple is one of the
          most revered Shakti Peethas, symbolizing divine feminine power and
          fertility.
        </p>
        <p>
          It celebrates the mystery of creation through the annual{" "}
          <span className="boldedTextNoUnderline">Ambubachi Mela </span>, where
          devotees gather from around the world.
        </p>
        <p>
          With Temple Dekho, you can now participate in the temple’s sacred
          pujas, offer{" "}
          <a href="/chadhavaTemples" className="linkedText">
            Chadawa
          </a>
          , or join in{" "}
          <a href="/livedarshan" className="linkedText">
            e-darshan
          </a>{" "}
          from anywhere on Earth.
        </p>
        <p>Connect to the Shakti that sustains the universe.</p>

        <h3>Jagannath Temple, Puri (Odisha)</h3>
        <p>
          A vibrant focal point of devotion, especially during the majestic
          Ratha Yatra when Lord Jagannath, Balabhadra and Subhadra are drawn out
          in grand chariots.
        </p>
        <p>
          Through our platform, you can connect with this tradition of motion
          and celebration, even from afar by participating in virtual offerings
          and witnessing the devotion in real time.
        </p>

        <h3>Mahakaleshwar Jyotirlinga, Ujjain (Madhya Pradesh)</h3>
        <p>
          Known for its extraordinary “Bhasma Aarti” and deep spiritual energy,
          this shrine of Lord Shiva stands in the ancient city of Ujjain.
        </p>
        <p>
          With Temple Dekho, you can{" "}
          <a href="/pujas" className="linkedText">
            book puja online
          </a>{" "}
          and participate in customised rituals held at this sacred site —
          bringing your devotion into your home with full reverence.
        </p>

        <h3>Kalighat Temple, Kolkata (West Bengal)</h3>
        <p>
          A shrine of Goddess Kali, Kalighat pulses with the intense power of
          the Divine Feminine. Its rituals, offerings and festivals resonate
          deeply with devotion.
        </p>
        <p>
          Temple Dekho opens the doors to this sacred centre, letting you
          sponsor pujas and partake in{" "}
          <a href="/chadhavaTemples" className="linkedText">
            online prasadam delivery
          </a>{" "}
          and live darshan even if you reside far from India.
        </p>

        <h2>How to explore with Temple Dekho</h2>
        <ol>
          <li>
            Browse our <a href="/temples">Famous Temples to Visit in India </a>{" "}
            section to find your desired shrine.
          </li>
          <li>
            Select the temple and learn about its rituals, timings and
            traditions.
          </li>
          <li>Choose the puja or darshan service you wish to book.</li>
          <li>
            Complete your booking and participate from your space, yet remain
            spiritually connected.
          </li>
          <li>
            Receive video recordings, prasad delivery or live streaming as per
            the service.
          </li>
        </ol>

        <h2>Why it matters</h2>
        <p>
          Spirituality is not a matter of location — it’s a matter of
          connection. Whether you are thousands of miles away or your schedule
          won’t allow a visit, your yearning for the Divine need not be unmet.
          At Temple Dekho, we merge tradition and technology so that you can
          feel that connection deeply. When you book a puja at any of these
          sacred temples, you’re engaging in a ritual that transcends geography,
          time and circumstance.
        </p>

        <h2>Temple Dekho’s Promise</h2>
        <p>
          We are committed to preserving the purity, transparency, and
          authenticity of every temple experience. From verified rituals to
          genuine temple partnerships, each service reflects our devotion to
          making faith easy, accessible, and meaningful for all.
        </p>

        <h2>Walk the Path of the Divine</h2>
        <p>Let ancient prayers guide your modern journey.</p>
        <p>
          <strong>Temple Dekho – Famous Temples in India:</strong> Where every
          click takes you closer to centuries of devotion.
        </p>
      </Collapse>

      <Button
        variant="outlined"
        sx={{ mt: "10px" }}
        onClick={handleToggle}
        color="primary"
      >
        {expanded ? "Read Less" : t(LOCALIZATION.Read_More)}
      </Button>
    </Box>
  );
};

export default TemplesContent;
